/**
 * How a computed figure was computed, opened from the figure itself.
 *
 * A margin or a growth rate is only as good as the numbers behind it, so the
 * question an analyst asks of "20%" is "out of what?". The KPI ledger answers
 * it with a Σ (`Monitoring/FormulaPopover`); here the computed value is the
 * trigger, and the popover writes the formula in the figures' own labels and
 * lists the value this run used for every input in this period — a growth
 * rate also names the prior year it grew from. An input the run does not
 * hold says so, which is why the figure could not be computed (DOP-527).
 */

import React from "react";
import { createPortal } from "react-dom";

import { useFloatingPanel } from "../../hooks/useFloatingPanel";
import type { DealScreeningFact } from "../../types/dealScreening";
import { formulaKeys } from "../Monitoring/FormulaPopover";
import {
  EMPTY_VALUE,
  NOT_FOUND,
  formatFactValue,
  formatTableValue,
  unconvertedCurrency,
} from "./dealScreeningFormat";

const PANEL_WIDTH = 288;

/** `${key}|${period ?? ""}` — one fact per figure per period. */
export const factSlot = (key: string, period: string | null): string =>
  `${key}|${period ?? ""}`;

export function indexFacts(
  facts: DealScreeningFact[],
): Map<string, DealScreeningFact> {
  return new Map(facts.map((fact) => [factSlot(fact.key, fact.period), fact]));
}

/** "FY2025A" → "FY2024"; the prior fiscal year, either actual or projected. */
const priorYearPrefix = (period: string | null): string | null => {
  const match = /^FY(\d{4})/.exec(period ?? "");
  return match ? `FY${Number(match[1]) - 1}` : null;
};

/**
 * The formula as a person reads it: labels for keys, "prior year" for
 * `[t-1]`, and the same typographic operators the ledger uses elsewhere.
 */
export function formulaInWords(
  formula: string,
  labelOf: (key: string) => string | undefined,
): string {
  return formula
    .replace(/\[t-1\]/g, " (prior year)")
    .replace(/[A-Za-z_][A-Za-z0-9_]*|[*/-]/g, (token) => {
      if (token === "*") return "×";
      if (token === "/") return "÷";
      if (token === "-") return "−";
      return labelOf(token) ?? token;
    });
}

export interface DerivedFormulaPopoverProps {
  /** The computed figure. */
  fact: DealScreeningFact;
  /** Every fact of the run by slot, so each input can be named and valued. */
  factsBySlot: ReadonlyMap<string, DealScreeningFact>;
  /** `table` prints "20%" bare; `text` prints "$1.5mm" with its unit. */
  variant?: "table" | "text";
}

interface InputRow {
  slot: string;
  label: string;
  text: string;
}

const DerivedFormulaPopover: React.FC<DerivedFormulaPopoverProps> = ({
  fact,
  factsBySlot,
  variant = "table",
}) => {
  const formula = fact.formula ?? "";
  const {
    open,
    setOpen,
    close,
    place,
    buttonRef,
    panelRef: popoverRef,
  } = useFloatingPanel(PANEL_WIDTH);

  const anyOf = (key: string): DealScreeningFact | undefined =>
    factsBySlot.get(factSlot(key, fact.period)) ??
    factsBySlot.get(factSlot(key, null)) ??
    Array.from(factsBySlot.values()).find((f) => f.key === key);
  const labelOf = (key: string): string | undefined => anyOf(key)?.label;

  const inputKeys = fact.inputs.length > 0 ? fact.inputs : formulaKeys(formula);
  const inputText = (input: DealScreeningFact | undefined): string => {
    if (!input) return NOT_FOUND;
    if (input.state === "gap" || input.state === "not_available")
      return NOT_FOUND;
    return formatFactValue(input.value, input.unit, unconvertedCurrency(input));
  };
  const rows: InputRow[] = [];
  for (const key of inputKeys) {
    const same =
      factsBySlot.get(factSlot(key, fact.period)) ??
      factsBySlot.get(factSlot(key, null));
    const label = same?.label ?? labelOf(key) ?? key;
    rows.push({
      slot: factSlot(key, same?.period ?? fact.period),
      label: same?.period ? `${label} ${same.period}` : label,
      text: inputText(same),
    });
    if (/\[t-1\]/.test(formula)) {
      const prefix = priorYearPrefix(fact.period);
      const prior = prefix
        ? Array.from(factsBySlot.values()).find(
            (f) => f.key === key && (f.period ?? "").startsWith(prefix),
          )
        : undefined;
      rows.push({
        slot: factSlot(key, prior?.period ?? `${prefix ?? "prior"}`),
        label: `${label} ${prior?.period ?? `${prefix ?? "prior year"} (prior year)`}`,
        text: inputText(prior),
      });
    }
  }
  const written = formulaInWords(formula, labelOf);
  const missing = fact.state === "gap" || fact.state === "not_available";
  const shown = missing
    ? EMPTY_VALUE
    : variant === "text"
      ? formatFactValue(fact.value, fact.unit, unconvertedCurrency(fact))
      : formatTableValue(fact.value, fact.unit, unconvertedCurrency(fact));

  return (
    <span className="relative inline-block align-baseline">
      <button
        type="button"
        ref={buttonRef}
        data-testid={`derived-${fact.key}${fact.period ? `-${fact.period}` : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`How ${fact.label}${fact.period ? ` ${fact.period}` : ""} was computed`}
        title={formula}
        onClick={() => (open ? close() : setOpen(true))}
        className={`text-left underline decoration-dotted decoration-1 underline-offset-2 ${
          missing ? "text-[#b54708]" : "decoration-[#004C4B]/50"
        }`}
      >
        {shown}
      </button>
      {open &&
        place &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label={`How ${fact.label} was computed`}
            data-testid={`derived-formula-${fact.key}`}
            style={{ top: place.top, left: place.left, width: PANEL_WIDTH }}
            className="fixed z-50 rounded-md border border-gray-200 bg-white p-2 text-left font-sans text-[12.5px] shadow-lg"
          >
            <p className="text-sm font-medium text-text-strong">{written}</p>
            <ul className="mt-1.5 space-y-0.5">
              {rows.map((row) => (
                <li
                  key={row.slot}
                  data-testid={`derived-input-${fact.key}-${row.slot}`}
                  className="flex items-baseline justify-between gap-3 text-sm text-text-muted"
                >
                  <span>{row.label}</span>
                  <span className="tabular-nums">{row.text}</span>
                </li>
              ))}
            </ul>
            {missing && fact.gap_reason && (
              <p className="mt-1.5 text-xs text-[#b54708]">{fact.gap_reason}</p>
            )}
          </div>,
          document.body,
        )}
    </span>
  );
};

export default DerivedFormulaPopover;
